import Observable from "zen-observable"
import type {ZenObservable} from "zen-observable"
import {ObservableQuery} from "@irati/utils/apollo"
import client from "@irati/utils/services/apollo"
import {waitPromise} from "@irati/utils/promise"
import {attemptToGetCode} from "@irati/utils/services/API/helpers/getCodeFromError"
import tokens from "../../../services/tokens"
import AuthStorage from "../storage"

interface Props<T> {
  storage: AuthStorage<T>
}

export const PBAUTHNotInitialised = "PBAUTHNotInitialised"
export type OnNext<T> = (auth: T) => void
export type OnError = (err: any) => void
export type OnComplete = () => void

type Listener<T> = Promise<ObservableQuery<T, any> | Observable<{data: T}>>

export default abstract class AuthAbstract<T> {
  private watcher?: ZenObservable.Subscription
  private watcherPromise?: Promise<void>
  private observers: any[] = []
  protected storage: AuthStorage<T>
  protected status: "started" | "stopped" = "stopped"
  private _data: T | null = null
  /** @description: Should we wipe all on listen failure?
   * - Most apps no, want to support offline
   * - Internal, typically yes
   */
  protected rejectOnListenFail = false

  shouldRedirectOnLogout = true

  constructor({storage}: Props<T>) {
    this.storage = storage
    this.makeInit()
  }

  protected set data(value: T | null) {
    this._data = value
    if (value) {
      this.storage.set(value)
      this.observers.forEach((observer) => {
        observer.next(value)
      })
    } else {
      this.storage.clear()
    }
  }

  protected get data() {
    return this._data
  }

  /** @description: How to initialise the auth stream. You normally just want to load from storage then call start.
   *  @notes: You may want to use isValid before you 'start' it.
   *  @example:
   *  return this.storage.get().then((data) => {
   *    return this.start(data)
   *  })
   */
  abstract init(): Promise<void>
  /** @description: What should we listen to?  */
  protected abstract query(data?: T): () => Listener<T>
  /** @description: Is the data valid for use? Typically is there at least a user object.  */
  abstract isValid(data: T): boolean

  /** @description: You've just logged in or think you have valid resume data, call this. */
  start(data?: T) {
    this.status = "started"
    return this.listen(this.query(data), data)
  }

  /** @description: User wants to log out or kill session, call this. */
  async stop() {
    this.status = "stopped"
    try {
      await client.clearStore()
    } finally {
      tokens.clear()
    }
    return this.unlisten({complete: true})
  }

  /** @description: The auth data, should be used sparingly, you should subscribe tho this. */
  get(): T {
    if (!this.data || !this.isValid(this.data)) {
      const error = new Error("[Auth]: Auth not initialised yet.")
      error.code = PBAUTHNotInitialised
      throw error
    }
    return this.data
  }

  /** @description: Listen for changes in the auth data. */
  subscribe(onNext: OnNext<T>, onError?: OnError, onComplete?: OnComplete) {
    return new Observable<T>((observer) => {
      this.storage.get().then((auth) => {
        observer.next(auth)
      })
      this.observers.push(observer)
      return () => {
        this.observers = this.observers.filter((t) => t !== observer)
      }
    }).subscribe(onNext, onError, onComplete)
  }

  /** @description: Has the stream been intialised. */
  get isInitialised() {
    return !!this.watcherPromise
  }

  protected listen(query: () => Listener<T>, initialData?: T): Promise<void> {
    if (initialData) {
      this.data = initialData
    }
    if (!this.watcherPromise) {
      this.watcherPromise = new Promise(async (resolve, reject) => {
        const q = await query()
        this.watcher = q.subscribe(
          (res) => {
            const data = <T>res.data
            this.data = data
            resolve()
          },
          (err) => {
            if (this.rejectOnListenFail) {
              console.log("[Auth]: resume watch query failed")
              return reject(err)
            }

            // if we get an error code, server said no so stop
            if (attemptToGetCode(err)) {
              return this.stop().then(() => resolve())
            } else if (initialData && this.isValid(initialData)) {
              // if no error code (offline) and we have enough data, poll until network connection returns
              this.unlisten()
              waitPromise(10000).then(() => {
                if (this.status !== "stopped") {
                  console.log("retrying...")
                  this.listen(query, initialData)
                }
              })
            }

            resolve()
          }
        )
      })
    }

    return this.watcherPromise
  }

  protected unlisten({complete = false} = {}) {
    if (this.watcher) {
      this.watcher.unsubscribe()
      this.watcher = undefined
      this.watcherPromise = undefined
    }
    if (complete) {
      this.data = null
      this.observers.forEach((observer) => {
        observer.complete()
      })
    }
  }

  /**
   * @description: To protect against mulitiple sync init calls, re-write the init function to only call once.
   */
  private makeInit() {
    let initResult: any

    const initFunc = this.init.bind(this)
    this.init = () => {
      if (!initResult) {
        initResult = initFunc()
      }

      return initResult.catch((err: any) => {
        initResult = null
        throw err
      })
    }
  }
}
